import { Link } from 'react-router';
import { useLingui } from '@lingui/react/macro';
import { MapPinOff } from 'lucide-react';

import { PageContainer } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/layout/PageHeader';
import { canUseMePortal } from '@/components/me/mePortal';
import { Button } from '@/components/ui/button';
import { useAccess } from '@/hooks/useAccess';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { firstWebModulePath } from '@/lib/modules';

/**
 * The page for an address the app shell has no route for. It says so instead
 * of silently showing another page, and leads to the user's start page: the
 * dashboard for an admin, the first granted module for an employee, the `/me`
 * portal for an employee who only has that.
 */
export function NotFoundPage() {
  const { t } = useLingui();
  const { isAdmin, hasModule, modules } = useAccess();
  const home = isAdmin
    ? '/'
    : (firstWebModulePath(modules) ?? (canUseMePortal({ isAdmin, hasModule }) ? '/me' : '/'));
  const title = t({ id: 'notFound.title', message: 'Page not found' });
  useDocumentTitle(title);

  return (
    <PageContainer width="form" className="py-12">
      <MapPinOff className="mb-4 h-10 w-10 text-muted-foreground" aria-hidden="true" />
      <PageHeader
        title={title}
        description={t({
          id: 'notFound.description',
          message: 'This address does not lead to a page. The link may be mistyped or out of date.',
        })}
      />
      <Button asChild>
        <Link to={home}>{t({ id: 'notFound.home', message: 'Go to start page' })}</Link>
      </Button>
    </PageContainer>
  );
}
